import { jsx as _jsx } from "react/jsx-runtime";
// Shared session store — production pattern for keeping the sidebar
// in sync with chat-page state changes.
//
// Problem this solves: a chat session's name can change in three
// places (user inline rename, server auto-suffix on collision, LLM
// background auto-rename). The sidebar that lists all sessions needs
// to reflect the new name immediately, without polling, without
// window-level custom events, and across multiple route trees
// (Workspace sidebar + the SessionList page at /p/:id/sessions).
//
// Production-standard fix: lift the sessions list into a React Context
// that lives ABOVE the router. Both Workspace and SessionList consume
// it; both the chat page's WS handler and the inline rename write to
// it. The sidebar re-renders naturally when the context value changes.
//
// One source of truth. No polling. No custom DOM events. Just React.
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from "react";
const SessionContext = createContext(null);
export function SessionProvider({ children }) {
    // Sessions keyed by project_id. We key by project rather than
    // maintaining a flat list because the chat page belongs to one
    // project at a time, and the sidebar can switch projects without
    // a remount.
    const [byProject, setByProject] = useState({});
    const [toast, setToast] = useState(null);
    // Auto-dismiss the toast after 4s.
    const toastTimer = useRef(null);
    const showToast = useCallback((t) => {
        if (toastTimer.current)
            clearTimeout(toastTimer.current);
        setToast(t);
        if (t) {
            toastTimer.current = setTimeout(() => setToast(null), 4000);
        }
    }, []);
    const value = useMemo(() => ({
        list: (projectId) => byProject[projectId] ?? [],
        setAll: (projectId, list) => {
            setByProject((cur) => ({ ...cur, [projectId]: list }));
        },
        rename: (sessionId, newName) => {
            setByProject((cur) => {
                const next = {};
                for (const [pid, arr] of Object.entries(cur)) {
                    next[pid] = arr.map((s) => s.id === sessionId ? { ...s, name: newName } : s);
                }
                return next;
            });
        },
        add: (projectId, session) => {
            setByProject((cur) => {
                const arr = cur[projectId] ?? [];
                // De-dupe: if a session with this id is already there, replace it
                // in place; otherwise prepend.
                const without = arr.filter((s) => s.id !== session.id);
                return { ...cur, [projectId]: [session, ...without] };
            });
        },
        remove: (sessionId) => {
            setByProject((cur) => {
                const next = {};
                for (const [pid, arr] of Object.entries(cur)) {
                    next[pid] = arr.filter((s) => s.id !== sessionId);
                }
                return next;
            });
        },
        toast,
        setToast: showToast,
    }), [byProject, toast, showToast]);
    return (_jsx(SessionContext.Provider, { value: value, children: children }));
}
export function useSessions() {
    const v = useContext(SessionContext);
    if (!v) {
        throw new Error("useSessions must be called inside <SessionProvider>");
    }
    return v;
}
